import { AxiosError, AxiosHeaders, type AxiosAdapter, type AxiosResponse } from 'axios';
import { AUTH_PORTAL, IS_INDEPENDENT_PORTAL } from '../app/portal.ts';
import './storage.ts';

function mockServiceOrigin(): string | undefined {
  const configured = typeof window === 'undefined' ? undefined : window.__QCDOC_PORTAL_CONFIG__?.mockServiceUrl;
  if (!IS_INDEPENDENT_PORTAL || !configured) return undefined;
  const url = new URL(configured);
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
    throw new Error('mockServiceUrl must be an HTTP(S) origin without credentials, path, query, or fragment.');
  }
  return url.origin;
}

const serviceAdapter: AxiosAdapter = async (config) => {
  const origin = mockServiceOrigin();
  if (!origin || !config.url) throw new Error('Shared mock service is not configured.');
  const url = new URL(config.url, `${origin}/`);
  for (const [key, raw] of Object.entries(config.params ?? {})) {
    if (raw === undefined || raw === null) continue;
    for (const value of Array.isArray(raw) ? raw : [raw]) url.searchParams.append(key, String(value));
  }
  const headers = new Headers();
  for (const [key, value] of Object.entries(AxiosHeaders.from(config.headers).toJSON())) {
    if (value !== undefined && value !== null) headers.set(key, String(value));
  }
  headers.set('X-QCDOC-Portal', AUTH_PORTAL);
  const method = String(config.method ?? 'GET').toUpperCase();
  const response = await fetch(url, {
    method,
    headers,
    body: ['GET', 'HEAD'].includes(method) || config.data === undefined
      ? undefined
      : typeof config.data === 'string' ? config.data : JSON.stringify(config.data),
    signal: config.signal instanceof AbortSignal ? config.signal : undefined,
  });
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); }
    catch { data = text; }
  }
  const result: AxiosResponse = {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: new AxiosHeaders(Object.fromEntries(response.headers.entries())),
    config,
  };
  if (!response.ok) {
    const message = typeof data === 'object' && data && 'message' in data ? String(data.message) : `Mock service request failed (${response.status}).`;
    throw new AxiosError(message, AxiosError.ERR_BAD_RESPONSE, config, undefined, result);
  }
  return result;
};

export const mockAdapter: AxiosAdapter = async (config) => {
  if (mockServiceOrigin()) return serviceAdapter(config);
  const runtime = await import('@qcdoc/mock-runtime/runtime.ts');
  const response = await runtime.mockAdapter(config);
  return { ...response, data: structuredClone(response.data) };
};